import { useState } from "react";
import { Alert, Button, Card, Table } from "../../ds";
import type { DeviceDetail, FirewallState, LanClient } from "../../lib/api";
import { useConfirmProbe } from "../../lib/hooks/useConfirmProbe";
import { useSection } from "../../lib/hooks/useSection";
import { SectionCard } from "../SectionCard";
import { EntryModal } from "./EntryModal";
import { ApplyConfirmModal, DraftToolbar, REVERT_IN } from "./FirewallDraft";
import {
  COLLECTION_KEYS,
  clientOptions,
  collections,
  firewallSpecs,
  type CollectionKey,
  type CollectionSpec,
  type FirewallEntry,
} from "./firewallSpecs";
import { ProbeBanner } from "./ProbeBanner";

// Sektion „Firewall“ → `firewall_get` lesen, `firewall_set`/`firewall_confirm`
// schreiben. Alle vier uci-Listen (Port-Freigaben, Regeln, Zonen,
// Weiterleitungen) hängen an EINEM Entwurf und gehen in EINEM `firewall_set`
// raus: der Auto-Rollback des Agents holt ganze Konfigurationen zurück, halbe
// Anwendungen wären nur eine Fehlerquelle.
//
// Der riskante Teil (Probe + Rollback) steckt in useConfirmProbe.

// Der Entwurf hat dieselbe Form wie der Snapshot — dann ist er zugleich der
// optimistische Stand während der Probe.
type Draft = FirewallState;

/** Eine der vier Listen, generisch gelesen. Die Spec kennt ihren echten Typ. */
const rowsOf = (draft: Draft, key: CollectionKey): FirewallEntry[] => draft[key];

interface Editing {
  collection: CollectionKey;
  index: number;
  entry: FirewallEntry;
}

function CollectionCard({
  spec,
  rows,
  onAdd,
}: {
  spec: CollectionSpec<FirewallEntry>;
  rows: FirewallEntry[];
  onAdd: () => void;
}) {
  return (
    <Card
      title={spec.title}
      subtitle={spec.description}
      bodyPadding={false}
      actions={
        <Button size="sm" variant="ghost" iconName="plus" onClick={onAdd}>
          {spec.singular}
        </Button>
      }
    >
      <Table
        columns={spec.columns}
        rows={rows}
        rowKey={(entry, i) => (entry as { id?: string }).id || `neu-${i}`}
        emptyMessage={spec.empty}
      />
    </Card>
  );
}

export function FirewallSection({ device }: { device: DeviceDetail }) {
  const section = useSection<FirewallState>(device, "firewall");
  // Die Geräte für die Ziel-IP kommen aus der LAN-Sektion — `lazy`, weil der
  // Frame erst fällig ist, wenn jemand wirklich eine Port-Freigabe aufmacht.
  // Was dabei hereinkommt, sieht der LAN-Tab danach mit (SectionSink).
  const lan = useSection<{ clients: LanClient[] }>(device, "lan", { lazy: true });
  const probe = useConfirmProbe(device.id, section, {
    setAction: "firewall_set",
    confirmAction: "firewall_confirm",
    revertIn: REVERT_IN,
  });

  // Lokaler Entwurf — erst „Anwenden“ schickt ihn zum Router.
  const [draft, setDraft] = useState<Draft | null>(null);
  const [editing, setEditing] = useState<Editing | null>(null);
  const [confirmOpen, setConfirmOpen] = useState(false);

  const current: Draft = draft ?? collections(section.data);
  const dirty = draft !== null;

  /** Dialog auf — und für Port-Freigaben vorher die Geräteliste besorgen. */
  const openEntry = (collection: CollectionKey, entry: FirewallEntry, index: number) => {
    if (collection === "redirects") lan.ensureFresh();
    setEditing({ collection, index, entry: { ...entry } });
  };

  const deleteEntry = (collection: CollectionKey, index: number) =>
    setDraft((d) => {
      const base = d ?? collections(section.data);
      return { ...base, [collection]: rowsOf(base, collection).filter((_, i) => i !== index) };
    });

  const specs = firewallSpecs(
    {
      zones: current.zones.map((z) => z.name).filter(Boolean),
      clients: clientOptions(lan.data?.clients ?? []),
      clientsLoading: lan.loading,
    },
    openEntry,
    deleteEntry,
  );

  function saveEntry() {
    if (!editing) return;
    setDraft((d) => {
      const base = d ?? collections(section.data);
      const rows = [...rowsOf(base, editing.collection)];
      if (editing.index < 0) rows.push(editing.entry);
      else rows[editing.index] = editing.entry;
      return { ...base, [editing.collection]: rows };
    });
    setEditing(null);
  }

  async function apply() {
    setConfirmOpen(false);
    // Während der Probe ist der Entwurf der aktive Stand auf dem Router.
    const optimistic = section.data ? { ...section.data, ...current } : undefined;
    if (await probe.apply({ ...current }, optimistic)) setDraft(null);
  }

  const entryCount = COLLECTION_KEYS.reduce((n, key) => n + rowsOf(current, key).length, 0);

  return (
    <>
      <SectionCard
        title="Firewall"
        description="Port-Freigaben, Regeln, Zonen und Weiterleitungen — Schreiben mit Auto-Rollback"
        section={section}
        deviceOnline={device.online}
        actions={
          <DraftToolbar
            dirty={dirty}
            applyDisabled={!dirty || !device.online || probe.phase === "probe"}
            applying={probe.applying}
            onDiscard={() => setDraft(null)}
            onApply={() => setConfirmOpen(true)}
          />
        }
        above={
          <>
            {probe.note && (
              <Alert variant={probe.note.variant} dismissible onDismiss={() => probe.setNote(null)}>
                {probe.note.text}
              </Alert>
            )}
            <ProbeBanner
              phase={probe.phase}
              secondsLeft={probe.secondsLeft}
              keeping={probe.keeping}
              hint="Der Router hat die neue Konfiguration testweise geladen und stellt die alte automatisch wieder her, wenn niemand bestätigt. Jetzt prüfen, ob Router und Geräte dahinter noch erreichbar sind."
              onKeep={() => void probe.keep()}
              onDismiss={probe.dismiss}
            />
            {dirty && (
              <Alert variant="info">
                Ungespeicherter Entwurf — auf dem Router ist noch der alte Stand aktiv.
              </Alert>
            )}
          </>
        }
      >
        {() => (
          <div className="flex flex-col gap-5">
            {specs.map((spec) => (
              <CollectionCard
                key={spec.key}
                spec={spec}
                rows={rowsOf(current, spec.key)}
                onAdd={() => openEntry(spec.key, spec.blank, -1)}
              />
            ))}
          </div>
        )}
      </SectionCard>

      <EntryModal
        fields={editing ? (specs.find((s) => s.key === editing.collection)?.fields ?? null) : null}
        singular={editing ? (specs.find((s) => s.key === editing.collection)?.singular ?? "") : ""}
        entry={editing?.entry ?? null}
        isNew={(editing?.index ?? -1) < 0}
        subtitle="Die Änderung geht erst mit „Anwenden“ auf Probe zum Router."
        onChange={(entry) => setEditing((e) => (e ? { ...e, entry } : e))}
        onSave={saveEntry}
        onClose={() => setEditing(null)}
      />

      <ApplyConfirmModal
        open={confirmOpen}
        entryCount={entryCount}
        busy={probe.applying}
        onClose={() => setConfirmOpen(false)}
        onApply={() => void apply()}
      />
    </>
  );
}
